/**
 * Archetypes / orientations — THE CANONICAL SET (3, not 4).
 *
 * Verbatim from the original prototype `ORIENTATION{}` (app.js L164-195) and the
 * mirrored reveal copy in `CONTENT.m2.reveal` (content.js L198-222). Keyed
 * `corp | invest | gen`. M2 derives the orientation; everything post-M2
 * (reveal card, site theming, recommendation set) keys off it.
 *
 * Source of truth: docs/EXPERIENCE-MODEL.md §1.
 */

export type ArchetypeId = 'corp' | 'invest' | 'gen';

export interface Archetype {
  /** Accent hex that themes the whole post-M2 experience. */
  accent: string;
  /** Reveal-card banner headline. */
  banner: string;
  /** Reveal-card body copy. */
  body: string;
  /** Body-class / `data-arch` token applied site-wide after reveal. */
  bodyClass: string;
  /** Display name shown on the reveal card (e.g. "THE SCALER"). */
  displayName: string;
  /** Stable key used everywhere (state, theming `data-arch`, recommendation lookup). */
  id: ArchetypeId;
  /** Human label for the orientation (e.g. "Corporate Finance analyst"). */
  label: string;
  /** One-line tagline under the display name. */
  tagline: string;
}

export const ARCHETYPES: Record<ArchetypeId, Archetype> = {
  corp: {
    id: 'corp',
    label: 'Corporate Finance analyst',
    displayName: 'THE SCALER',
    tagline: 'Builds businesses from inside.',
    accent: '#0F4D3A',
    bodyClass: 'arch-corp',
    banner: 'You think like a Corporate Finance analyst.',
    body: "You're drawn to operational questions, internal decisions, and helping organizations improve. The FP&A and private company valuation tracks are your home.",
  },
  invest: {
    id: 'invest',
    label: 'Investment analyst',
    displayName: 'THE ANALYST',
    tagline: 'Calls the moves before the market.',
    accent: '#7C3AED',
    bodyClass: 'arch-invest',
    banner: 'You think like an Investment analyst.',
    body: "You're drawn to market questions, external bets, and evaluating whether a price is right. The equity research and VC tracks are your home.",
  },
  gen: {
    id: 'gen',
    label: 'Generalist',
    displayName: 'THE POLYMATH',
    tagline: 'Fluent in both languages of finance.',
    accent: '#B8860B',
    bodyClass: 'arch-gen',
    banner: "You're a Generalist, a rare orientation.",
    body: "Both tracks will suit you, and you'll be more versatile than most.",
  },
};

/** Ordered list of all archetype ids (corp, invest, gen). */
export const ARCHETYPE_IDS: readonly ArchetypeId[] = ['corp', 'invest', 'gen'] as const;
